/**
 * PDF da proposta com texto vetorial (pesquisável, ~10 pt) — jsPDF + jspdf-autotable.
 * Imagens de /api/upload/: carregadas com JWT (fetchUploadBlob), como AuthenticatedUploadImage.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { requiresUploadBlobAuth, fetchUploadBlob } from "@/api/apiClient";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  removerReferenciasEdital,
  formatReferentePregao,
} from "@/utils/removerReferenciasEdital";
import { getPreposicaoInstituicaoDisplay } from "@/utils/preposicaoInstituicao";
import { getSubtotalLabelColSpan } from "@/utils/propostaDisplay";
import {
  ASSINATURA_CORES,
  iniciaisNomeCompleto,
  formatCnpjParaExibicao,
} from "@/utils/empresaAssinatura";
import {
  TIPO_LINHA,
  getTipoLinha,
  aplicarColunasCalculadas,
  somarColunaAteSubtotal,
} from "@/utils/propostaItensCalculations";
import { htmlToPlainTextForPdf } from "@/lib/htmlPlainText";
import { captureRodapeTextoHtmlForPdf } from "@/lib/rodapeTextoPdfRaster";

const HEAD_RGB = [30, 41, 59];
export const SIDE_MARGIN = 12;
const BODY_PT = 10;
const SMALL_PT = 9;
const BOTTOM_SAFE = 14;
/** Espaço acima da linha “Página X de Y” (desenhada depois a ~h−6 mm). */
const PAGE_FOOTER_RESERVE_MM = 12;
/** Folga abaixo do bloco medido do cabeçalho (imagem+texto); cobre métricas reais do jsPDF e arredondamentos. */
export const CABECALHO_RESERVE_EXTRA_MM = 8;

/**
 * Altura de linha do texto do cabeçalho (alinhada às métricas internas do jsPDF, não valor fixo).
 */
function getCabecalhoBodyLineHeightMm(doc) {
  doc.setFontSize(BODY_PT);
  doc.setFont("helvetica", "normal");
  const fs = doc.internal.getFontSize();
  const sf = doc.internal.scaleFactor;
  const lhf =
    typeof doc.getLineHeightFactor === "function"
      ? doc.getLineHeightFactor()
      : 1.15;
  return (fs / sf) * lhf;
}

/**
 * Margem inferior reservada para texto/tabelas; com rodapé institucional inclui altura medida + faixa da numeração.
 * Resetada ao sair de `buildPropostaPdfTextoReal` para não afetar outros PDFs (ex.: atestado com `signatureBlockPlaceAssinaturaImage`).
 */
let _contentBottomReserveMm = BOTTOM_SAFE;
/** Espaço reservado no topo (cabeçalho institucional repetido em cada página). */
let _contentTopReserveMm = 0;

/** Para PDFs que reutilizam helpers deste arquivo (ex.: assinatura no atestado) com o mesmo recuo superior. */
export function setPdfLayoutTopReserveMm(mm) {
  _contentTopReserveMm = Math.max(0, Number(mm) || 0);
}

/** Margem inferior usada por `ensureY` e por PDFs que importam `signatureBlockPlaceAssinaturaImage` (ex.: declaração). */
export function getPdfLayoutBottomReserveMm() {
  return _contentBottomReserveMm;
}

/**
 * Define margem inferior global do layout (rodapé + numeração). `null`/`undefined` restaura o padrão (14 mm).
 */
export function setPdfLayoutBottomReserveMm(mm) {
  if (mm === null || mm === undefined) {
    _contentBottomReserveMm = BOTTOM_SAFE;
    return;
  }
  const v = Number(mm);
  _contentBottomReserveMm = Number.isFinite(v) ? v : BOTTOM_SAFE;
}
/**
 * Altura estimada do bloco de assinatura (layout alinhado à tela: linha, avatar + nome, cargo, razão, CNPJ).
 */
export function estimateSignatureBlockHeightMm(hasAssinaturaImage) {
  const base =
    6 + // "Atenciosamente," + gap
    9 + // imagem + linha + gap curto
    26 + // círculo iniciais + quatro linhas ao lado
    4; // folga
  const imageMm = hasAssinaturaImage ? 26 : 0;
  return base + imageMm;
}

function disp(v, fallback = "Não informado") {
  if (v === null || v === undefined || v === "") return fallback;
  return String(v);
}

function formatCurrency(value) {
  if (value === undefined || value === null) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

function formatCertameLongo(edital) {
  const d = edital?.data_abertura || edital?.dataAbertura;
  if (!d) return "Não informado";
  try {
    return format(new Date(d), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return "Não informado";
  }
}

function configRazao(config) {
  return (
    config?.razao_social ||
    config?.razaoSocial ||
    ""
  ).trim();
}

function configCnpj(config) {
  return String(config?.cnpj || config?.CNPJ || "").trim();
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * Carrega URL de imagem para data URL (upload autenticado ou URL pública).
 */
export async function loadImageDataUrlForPdf(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (trimmed.startsWith("data:")) return trimmed;
  try {
    if (requiresUploadBlobAuth(trimmed)) {
      const blob = await fetchUploadBlob(trimmed);
      return await blobToDataUrl(blob);
    }
    const abs =
      trimmed.startsWith("/") && typeof window !== "undefined"
        ? `${window.location.origin}${trimmed}`
        : trimmed;
    const res = await fetch(abs, { credentials: "include", mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return null;
    return await blobToDataUrl(blob);
  } catch (e) {
    console.warn("[Proposta PDF] Falha ao carregar imagem:", url, e);
    return null;
  }
}

/**
 * Insere imagem centralizada; retorna novo Y (base inferior da imagem + margem).
 */
function addImageDataUrlCentered(doc, dataUrl, y, contentW, maxW, maxH, options = {}) {
  void options;
  if (!dataUrl) return y;
  const props = doc.getImageProperties(dataUrl);
  const iw = props.width;
  const ih = props.height;
  let w = Math.min(maxW, contentW);
  let h = (ih * w) / iw;
  if (h > maxH) {
    h = maxH;
    w = (iw * h) / ih;
  }
  const x = SIDE_MARGIN + (contentW - w) / 2;
  const fmt = String(dataUrl).includes("image/png") ? "PNG" : "JPEG";
  y = ensureY(doc, y, h + 2);
  doc.addImage(dataUrl, fmt, x, y, w, h);
  return y + h + 3;
}

/**
 * Imagem da assinatura acima da linha, alinhada à esquerda com o texto (margem do documento).
 * Linha um pouco mais curta que a faixa central antiga (~34% da largura útil, máx. 68 mm).
 * @returns {{ y: number, sigLineW: number }}
 */
export function signatureBlockPlaceAssinaturaImage(doc, assImg, y, contentW, options = {}) {
  const align = options.align === "center" ? "center" : "left";
  const defaultSigLineW = Math.min(contentW * 0.34, 68);
  if (!assImg) {
    return { y, sigLineW: defaultSigLineW };
  }
  const maxW = Math.min(75, contentW * 0.55);
  const maxH = 20;
  const props = doc.getImageProperties(assImg);
  const iw = props.width;
  const ih = props.height;
  let w = Math.min(maxW, contentW);
  let h = (ih * w) / iw;
  if (h > maxH) {
    h = maxH;
    w = (iw * h) / ih;
  }
  const sigLineW = Math.min(contentW, Math.max(w + 4, defaultSigLineW));
  const fmt = String(assImg).includes("image/png") ? "PNG" : "JPEG";
  const yImgTop = y;
  y = ensureY(doc, yImgTop, h + 2);
  const x =
    align === "center"
      ? SIDE_MARGIN + (sigLineW - w) / 2
      : SIDE_MARGIN;
  doc.addImage(assImg, fmt, x, y, w, h);
  /** Linha na base da área da imagem (sobreposta ~1,5 mm), como assinatura no papel. */
  const yLine = y + h - 1.5;
  return { y: yLine, sigLineW };
}

export function ensureY(doc, y, needMm) {
  const h = doc.internal.pageSize.getHeight();
  if (y + needMm > h - _contentBottomReserveMm) {
    doc.addPage();
    return SIDE_MARGIN + _contentTopReserveMm + 6;
  }
  return y;
}

/**
 * Altura total (mm) do bloco de cabeçalho (imagem até 24 mm + texto), alinhada ao desenho em `addCabecalhoEmTodasAsPaginas`.
 */
export function measureCabecalhoBlockHeightMm(doc, cabImg, cabecalhoTexto, contentW) {
  let y = 0;
  if (cabImg) {
    const props = doc.getImageProperties(cabImg);
    let w = Math.min(contentW, contentW);
    let h = (props.height * w) / props.width;
    const maxH = 24;
    if (h > maxH) {
      h = maxH;
      w = (props.width * h) / props.height;
    }
    y += h + 3;
  }
  const txtRaw = cabecalhoTexto && String(cabecalhoTexto).trim();
  const txt = txtRaw ? htmlToPlainTextForPdf(txtRaw) : "";
  if (txt) {
    doc.setFontSize(BODY_PT);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(txt, contentW);
    const lh = getCabecalhoBodyLineHeightMm(doc);
    y += lines.length * lh + 1;
  }
  return y;
}

function drawCabecalhoOnPageAt(doc, cabImg, cabecalhoTexto, contentW, yTop) {
  let y = yTop;
  if (cabImg) {
    const props = doc.getImageProperties(cabImg);
    const iw = props.width;
    const ih = props.height;
    let w = Math.min(contentW, contentW);
    let h = (ih * w) / iw;
    const maxH = 24;
    if (h > maxH) {
      h = maxH;
      w = (iw * h) / ih;
    }
    const x = SIDE_MARGIN + (contentW - w) / 2;
    const fmt = String(cabImg).includes("image/png") ? "PNG" : "JPEG";
    doc.addImage(cabImg, fmt, x, y, w, h);
    y += h + 3;
  }
  const txtRaw = cabecalhoTexto && String(cabecalhoTexto).trim();
  const txt = txtRaw ? htmlToPlainTextForPdf(txtRaw) : "";
  if (txt) {
    doc.setFontSize(BODY_PT);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    const lines = doc.splitTextToSize(txt, contentW);
    const lh = getCabecalhoBodyLineHeightMm(doc);
    for (const line of lines) {
      doc.text(line, SIDE_MARGIN, y);
      y += lh;
    }
    y += 1;
  }
}

/** Desenha o cabeçalho institucional no topo de cada página (após o conteúdo estar fechado). */
export function addCabecalhoEmTodasAsPaginas(doc, cabImg, cabecalhoTexto, contentW) {
  const cabPlain = cabecalhoTexto ? htmlToPlainTextForPdf(String(cabecalhoTexto)).trim() : "";
  if (!cabImg && !cabPlain) return;
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawCabecalhoOnPageAt(doc, cabImg, cabecalhoTexto, contentW, SIDE_MARGIN);
  }
}

/**
 * Altura do bloco de rodapé (imagem + texto), alinhada a `addRodapeAoFundoDaPagina`.
 * @param {null|undefined|{ dataUrl: string, drawWidthMm: number, drawHeightMm: number }} rodapeRaster — HTML rico rasterizado (sem `rodImg`)
 */
export function measureRodapeBlockHeightMm(doc, rodImg, rodapeTexto, contentW, rodapeRaster) {
  if (!rodImg && rodapeRaster?.dataUrl) {
    return rodapeRaster.drawHeightMm;
  }
  let imgH = 0;
  if (rodImg) {
    const props = doc.getImageProperties(rodImg);
    let w = Math.min(contentW, contentW);
    let h = (props.height * w) / props.width;
    const maxH = 22;
    if (h > maxH) {
      h = maxH;
    }
    imgH = h;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const rodPlain = rodapeTexto ? htmlToPlainTextForPdf(String(rodapeTexto)) : "";
  const lines = rodPlain ? doc.splitTextToSize(rodPlain, contentW) : [];
  const lineH = 3.8;
  const textH = lines.length ? lines.length * lineH + 2 : 0;
  const gapEntreImgTexto = 2;
  return (
    (rodImg ? imgH : 0) +
    (rodImg && lines.length ? gapEntreImgTexto : 0) +
    textH
  );
}

/**
 * @param {null|undefined|{ dataUrl: string, drawWidthMm: number, drawHeightMm: number }} rodapeRaster
 */
export function addRodapeEmTodasAsPaginas(doc, rodImg, rodapeTexto, contentW, rodapeRaster) {
  const rodPlain = rodapeTexto ? htmlToPlainTextForPdf(String(rodapeTexto)).trim() : "";
  const hasRaster = Boolean(!rodImg && rodapeRaster?.dataUrl);
  if (!rodImg && !hasRaster && !rodPlain) return;
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    addRodapeAoFundoDaPagina(doc, rodImg, rodapeTexto, contentW, rodapeRaster);
  }
}

/**
 * Rodapé (imagem e/ou texto) no fundo da página atual.
 * @param {null|undefined|{ dataUrl: string, drawWidthMm: number, drawHeightMm: number }} rodapeRaster
 */
function addRodapeAoFundoDaPagina(doc, rodImg, rodapeTexto, contentW, rodapeRaster) {
  const rodPlainGuard = rodapeTexto ? htmlToPlainTextForPdf(String(rodapeTexto)).trim() : "";
  const hasRaster = Boolean(!rodImg && rodapeRaster?.dataUrl);
  if (!rodImg && !hasRaster && !rodPlainGuard) return;

  const pageH = doc.internal.pageSize.getHeight();
  let imgW = 0;
  let imgH = 0;
  let imgX = SIDE_MARGIN;
  let fmt = "PNG";
  let dataUrl = null;

  if (rodImg) {
    dataUrl = rodImg;
    const props = doc.getImageProperties(rodImg);
    let w = Math.min(contentW, contentW);
    let h = (props.height * w) / props.width;
    const maxH = 22;
    if (h > maxH) {
      h = maxH;
      w = (props.width * h) / props.height;
    }
    imgW = w;
    imgH = h;
    imgX = SIDE_MARGIN + (contentW - w) / 2;
    fmt = String(rodImg).includes("image/png") ? "PNG" : "JPEG";
  } else if (hasRaster) {
    dataUrl = rodapeRaster.dataUrl;
    imgW = rodapeRaster.drawWidthMm;
    imgH = rodapeRaster.drawHeightMm;
    imgX = SIDE_MARGIN + (contentW - imgW) / 2;
    fmt = "PNG";
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const rodPlain = rodapeTexto ? htmlToPlainTextForPdf(String(rodapeTexto)) : "";
  const usePlainLines = rodImg || !hasRaster;
  const lines =
    usePlainLines && rodPlain ? doc.splitTextToSize(rodPlain, contentW) : [];
  const lineH = 3.8;
  const textH = lines.length ? lines.length * lineH + 2 : 0;
  const gapEntreImgTexto = 2;
  const hasInstImg = Boolean(rodImg || hasRaster);
  const imgDrawH = rodImg || hasRaster ? imgH : 0;
  const totalBlockH =
    imgDrawH +
    (rodImg && lines.length ? gapEntreImgTexto : 0) +
    textH;

  const blockTop = pageH - PAGE_FOOTER_RESERVE_MM - totalBlockH;

  let y = blockTop;
  if (hasInstImg && dataUrl) {
    doc.addImage(dataUrl, fmt, imgX, y, imgW, imgH);
    y += imgDrawH;
    if (rodImg && lines.length) y += gapEntreImgTexto;
  }
  if (lines.length) {
    doc.setTextColor(80, 80, 80);
    for (const line of lines) {
      doc.text(line, SIDE_MARGIN, y);
      y += lineH;
    }
    doc.setTextColor(0, 0, 0);
  }
}

/**
 * Altura em mm que `paragraph` consome (BODY_PT + helvetica normal), sem desenhar.
 * Deve coincidir com o retorno efetivo: n * lineHeight + 1 por bloco.
 */
function estimateParagraphBlockHeightMm(doc, text, width, lineHeight = 4.8) {
  if (!text) return 0;
  doc.setFontSize(BODY_PT);
  doc.setFont("helvetica", "normal");
  const lines = doc.splitTextToSize(String(text), width);
  return lines.length * lineHeight + 1;
}

/**
 * Desenha parágrafo com quebra de linha; retorna novo Y.
 */
function paragraph(doc, text, x, y, width, lineHeight = 4.8) {
  if (!text) return y;
  doc.setFontSize(BODY_PT);
  doc.setFont("helvetica", "normal");
  const lines = doc.splitTextToSize(String(text), width);
  for (const line of lines) {
    y = ensureY(doc, y, lineHeight);
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y + 1;
}

function sectionTitle(doc, title, x, y, width) {
  y = ensureY(doc, y, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(BODY_PT);
  doc.setTextColor(...HEAD_RGB);
  doc.text(title, x, y);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  return y + 6;
}

function buildColunasConfig(proposta) {
  if (proposta.colunas_itens?.length > 0) {
    return proposta.colunas_itens.filter((c) => c.visivel !== false);
  }
  return [
    { id: "item", nome: "Item", tipo: "numero" },
    { id: "descricao", nome: "Descrição", tipo: "texto_longo" },
    { id: "unidade", nome: "UND", tipo: "texto" },
    { id: "quantidade", nome: "Qtd", tipo: "numero" },
    { id: "valor_unitario", nome: "Valor Unitário", tipo: "valor" },
    { id: "valor_total", nome: "Total", tipo: "valor" },
  ];
}

function propostaItensTemGrupo(itens) {
  return (itens || []).some(
    (row) => getTipoLinha(row) === TIPO_LINHA.CABECALHO_GRUPO
  );
}

function pushLinhaCabecalhoColunasPdf(body, colunasConfig) {
  body.push(
    colunasConfig.map((col) => ({
      content: col.nome,
      styles: {
        fillColor: HEAD_RGB,
        textColor: 255,
        fontStyle: "bold",
        fontSize: SMALL_PT,
        halign:
          col.tipo === "valor" || col.tipo === "calculada"
            ? "right"
            : col.tipo === "numero"
              ? "center"
              : "left",
      },
    }))
  );
}

function formatarCelulaPdf(item, coluna, colunasConfig) {
  if (coluna.tipo === "calculada") {
    const vals = aplicarColunasCalculadas(item, colunasConfig);
    const v = vals[coluna.id];
    if (v === null || v === undefined || v === "") return "—";
    return formatCurrency(v);
  }
  const valor = item[coluna.id];
  if (valor === null || valor === undefined || valor === "") return "—";
  if (coluna.tipo === "valor") return formatCurrency(valor);
  if (coluna.tipo === "numero") {
    const n = Number(valor);
    return Number.isFinite(n)
      ? new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 6 }).format(n)
      : String(valor);
  }
  return String(valor);
}

function buildItensTableBody(proposta, colunasConfig, inserirCabecalhosPorGrupo) {
  const body = [];
  const itens = proposta.itens || [];
  const n = colunasConfig.length;

  if (
    inserirCabecalhosPorGrupo &&
    itens.length > 0 &&
    getTipoLinha(itens[0]) !== TIPO_LINHA.CABECALHO_GRUPO
  ) {
    pushLinhaCabecalhoColunasPdf(body, colunasConfig);
  }

  for (let index = 0; index < itens.length; index++) {
    const item = itens[index];
    const tipoLinha = getTipoLinha(item);

    if (tipoLinha === TIPO_LINHA.CABECALHO_GRUPO) {
      body.push([
        {
          content: item.titulo_grupo || "Grupo",
          colSpan: n,
          styles: {
            fillColor: [254, 243, 199],
            fontStyle: "bold",
            textColor: [120, 53, 15],
          },
        },
      ]);
      if (inserirCabecalhosPorGrupo) {
        pushLinhaCabecalhoColunasPdf(body, colunasConfig);
      }
      continue;
    }

    if (tipoLinha === TIPO_LINHA.SUBTOTAL) {
      const labelSpan = getSubtotalLabelColSpan(colunasConfig);
      const row = [
        {
          content: "Subtotal do grupo",
          colSpan: labelSpan,
          styles: { fontStyle: "bold", fillColor: [241, 245, 249] },
        },
      ];
      for (const col of colunasConfig.slice(labelSpan)) {
        if (col.tipo === "texto" || col.tipo === "texto_longo") {
          row.push({ content: "", styles: { fillColor: [241, 245, 249] } });
        } else {
          const soma = somarColunaAteSubtotal(
            itens,
            index,
            col,
            colunasConfig
          );
          const txt =
            col.tipo === "numero"
              ? new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 6 }).format(
                  soma
                )
              : formatCurrency(soma);
          row.push({
            content: txt,
            styles: {
              fontStyle: "bold",
              halign: "right",
              fillColor: [241, 245, 249],
            },
          });
        }
      }
      body.push(row);
      continue;
    }

    body.push(
      colunasConfig.map((col) => ({
        content: formatarCelulaPdf(item, col, colunasConfig),
        styles: {
          halign:
            col.tipo === "valor" || col.tipo === "calculada"
              ? "right"
              : col.tipo === "numero"
                ? "center"
                : "left",
        },
      }))
    );
  }

  return body;
}

export function addPageNumbers(doc) {
  const total = doc.internal.getNumberOfPages();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Página ${i} de ${total}`, w / 2, h - 6, { align: "center" });
    doc.setTextColor(0, 0, 0);
  }
}

/**
 * @param {object} p
 * @param {object} p.proposta
 * @param {object|null} p.edital
 * @param {object} p.config
 * @param {string} p.localProposta — ex.: "Recife/PE"
 * @param {string} p.dataPropostaExtenso — data por extenso já formatada
 */
export async function buildPropostaPdfTextoReal({
  proposta,
  edital,
  config,
  localProposta,
  dataPropostaExtenso,
}) {
  const [cabImg, rodImg, assImg] = await Promise.all([
    loadImageDataUrlForPdf(config?.cabecalho_url ?? config?.cabecalhoUrl),
    loadImageDataUrlForPdf(config?.rodape_url ?? config?.rodapeUrl),
    loadImageDataUrlForPdf(config?.assinatura_url ?? config?.assinaturaUrl),
  ]);

  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - 2 * SIDE_MARGIN;
  const rodapeTexto = config?.rodape_texto;
  let rodapeRaster = null;
  if (!rodImg && rodapeTexto && String(rodapeTexto).trim()) {
    rodapeRaster = await captureRodapeTextoHtmlForPdf({
      html: rodapeTexto,
      contentWidthMm: contentW,
    });
  }
  const rodapePlain = rodapeTexto ? htmlToPlainTextForPdf(String(rodapeTexto)).trim() : "";
  const hasRodape = Boolean(rodImg || rodapeRaster?.dataUrl || rodapePlain);
  _contentBottomReserveMm = hasRodape
    ? PAGE_FOOTER_RESERVE_MM +
      measureRodapeBlockHeightMm(doc, rodImg, rodapeTexto, contentW, rodapeRaster)
    : BOTTOM_SAFE;

  const cabTexto = config?.cabecalho_texto;
  const cabPlain = cabTexto ? htmlToPlainTextForPdf(String(cabTexto)).trim() : "";
  const hasCabecalho = Boolean(cabImg || cabPlain);
  _contentTopReserveMm = hasCabecalho
    ? measureCabecalhoBlockHeightMm(doc, cabImg, cabTexto, contentW) +
      CABECALHO_RESERVE_EXTRA_MM
    : 0;

  /** Início vertical do corpo; em páginas seguintes o autotable usa margin.top (não startY). */
  const contentStartYMm = SIDE_MARGIN + _contentTopReserveMm;

  try {
  let y = contentStartYMm;

  const instPdf = getPreposicaoInstituicaoDisplay(proposta.ao_instituicao);
  const dadosIniciais = [
    [instPdf.preposicao, disp(removerReferenciasEdital(instPdf.nomeExibicao))],
    [
      "Agente de contratação",
      disp(removerReferenciasEdital(proposta.agente_contratacao)),
    ],
    ["Referente", disp(formatReferentePregao(proposta))],
    ["Processo", disp(removerReferenciasEdital(proposta.processo_numero))],
    ["Data do certame", formatCertameLongo(edital)],
    [
      "Hora",
      disp(edital?.hora_abertura || edital?.horaAbertura),
    ],
    ["Sistema", disp(removerReferenciasEdital(proposta.sistema))],
    ["Identificação", disp(removerReferenciasEdital(proposta.identificacao))],
  ];

  autoTable(doc, {
    startY: y,
    showHead: "never",
    body: dadosIniciais,
    styles: { fontSize: SMALL_PT, cellPadding: 1.2, overflow: "linebreak" },
    columnStyles: { 0: { cellWidth: 48 }, 1: { cellWidth: contentW - 48 } },
    margin: {
      top: contentStartYMm,
      left: SIDE_MARGIN,
      right: SIDE_MARGIN,
      bottom: _contentBottomReserveMm,
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 0) {
        data.cell.styles.fontStyle = "bold";
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  y = sectionTitle(doc, "PROPOSTA DE PREÇOS COMERCIAL", SIDE_MARGIN, y, contentW);
  const intro = `Prezado(a) Senhor(a), a empresa ${disp(configRazao(config))}, inscrita no CNPJ nº ${disp(configCnpj(config))}, apresenta formalmente esta proposta de preços para a aquisição dos bens e/ou serviços indicados, nos termos do Termo de Referência e do edital em epígrafe.`;
  y = paragraph(doc, intro, SIDE_MARGIN, y, contentW);

  const enderecoBloco = `Sede / endereço: ${disp(config.endereco_completo)}\n${disp(config.municipio)} / ${disp(config.uf)} — CEP ${disp(config.cep)}.\nTelefone: ${disp(config.telefone_1)}.\nRepresentante legal: ${disp(config.socio_nome)} subscreve esta proposta e declara ciência das condições do certame.`;
  y = paragraph(doc, enderecoBloco, SIDE_MARGIN, y, contentW);

  y = sectionTitle(doc, "Objeto", SIDE_MARGIN, y, contentW);
  y = paragraph(
    doc,
    disp(removerReferenciasEdital(proposta.objeto), "Não informado"),
    SIDE_MARGIN,
    y,
    contentW
  );

  const colunasConfig = buildColunasConfig(proposta);
  if (proposta.itens?.length) {
    y = sectionTitle(doc, "Composição de preços", SIDE_MARGIN, y, contentW);
    const temGrupo = propostaItensTemGrupo(proposta.itens);
    const body = buildItensTableBody(
      proposta,
      colunasConfig,
      temGrupo
    );
    autoTable(doc, {
      startY: y,
      ...(temGrupo
        ? { showHead: "never" }
        : {
            head: [colunasConfig.map((c) => c.nome)],
            showHead: "everyPage",
          }),
      body,
      tableWidth: contentW,
      styles: {
        fontSize: SMALL_PT,
        cellPadding: 1,
        overflow: "linebreak",
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: HEAD_RGB,
        textColor: 255,
        fontStyle: "bold",
        fontSize: SMALL_PT,
      },
      margin: {
        top: contentStartYMm,
        left: SIDE_MARGIN,
        right: SIDE_MARGIN,
        bottom: _contentBottomReserveMm,
      },
      tableLineColor: [200, 200, 200],
      tableLineWidth: 0.1,
      didParseCell: (data) => {
        if (data.section === "body" && data.row.index % 2 === 1) {
          const c = data.cell.raw;
          if (c && typeof c === "object" && c.styles?.fillColor) return;
          if (!data.cell.styles.fillColor) {
            data.cell.styles.fillColor = [248, 250, 252];
          }
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  } else {
    y = paragraph(doc, "Nenhum item na proposta.", SIDE_MARGIN, y, contentW);
  }

  if (proposta.valor_total_proposta > 0) {
    y = sectionTitle(doc, "Valor total da proposta", SIDE_MARGIN, y, contentW);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    y = ensureY(doc, y, 8);
    doc.text(formatCurrency(proposta.valor_total_proposta), SIDE_MARGIN, y, {
      align: "left",
    });
    y += 7;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(SMALL_PT);
    const ex = disp(
      proposta.texto_valor_total,
      "Valor global da proposta de preços"
    );
    y = paragraph(doc, `(${ex})`, SIDE_MARGIN, y, contentW);
    doc.setFont("helvetica", "normal");
  }

  if (proposta.campos_extras?.length) {
    y = sectionTitle(doc, "Demais valores", SIDE_MARGIN, y, contentW);
    for (const campo of proposta.campos_extras) {
      const linha = `${disp(campo.descricao)} — ${formatCurrency(campo.valor)}`;
      y = paragraph(doc, linha, SIDE_MARGIN, y, contentW);
      if (campo.extenso) {
        doc.setFont("helvetica", "italic");
        y = paragraph(doc, campo.extenso, SIDE_MARGIN, y, contentW);
        doc.setFont("helvetica", "normal");
      }
    }
  }

  if (proposta.observacoes) {
    y = sectionTitle(doc, "Observações", SIDE_MARGIN, y, contentW);
    y = paragraph(doc, proposta.observacoes, SIDE_MARGIN, y, contentW);
  }

  y = sectionTitle(doc, "PRAZOS E OUTROS", SIDE_MARGIN, y, contentW);
  const prazos = [
    [
      "Validade da proposta",
      disp(proposta.prazoValidade ?? proposta.prazo_validade),
    ],
    ["Entrega", disp(proposta.prazoEntrega ?? proposta.prazo_entrega)],
    [
      "Vigência do contrato",
      disp(proposta.prazoVigencia ?? proposta.prazo_vigencia),
    ],
    [
      "Pagamento",
      disp(proposta.prazoPagamento ?? proposta.prazo_pagamento),
    ],
  ];
  const prazosExtrasRaw = proposta.prazosExtras ?? proposta.prazos_extras;
  if (Array.isArray(prazosExtrasRaw)) {
    for (const row of prazosExtrasRaw) {
      const titulo = row?.titulo != null ? String(row.titulo).trim() : "";
      const conteudo = row?.conteudo != null ? String(row.conteudo).trim() : "";
      if (!titulo && !conteudo) continue;
      prazos.push([disp(titulo || "—"), disp(conteudo || "—")]);
    }
  }
  autoTable(doc, {
    startY: y,
    body: prazos,
    styles: { fontSize: SMALL_PT, cellPadding: 1 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 52 } },
    margin: {
      top: contentStartYMm,
      left: SIDE_MARGIN,
      right: SIDE_MARGIN,
      bottom: _contentBottomReserveMm,
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  y = sectionTitle(doc, "Declarações", SIDE_MARGIN, y, contentW);
  const decl =
    "Informamos que nos preços apresentados estão computados todos os custos necessários à prestação dos serviços ou ao fornecimento, incluindo impostos, encargos trabalhistas e previdenciários, demais encargos fiscais e comerciais, taxas, seguros, deslocamentos e demais que incidam direta ou indiretamente.";
  y = paragraph(doc, decl, SIDE_MARGIN, y, contentW);

  y = sectionTitle(doc, "Dados bancários", SIDE_MARGIN, y, contentW);
  const banco = [
    ["Conta corrente", disp(config.banco_conta)],
    ["Código do banco", disp(config.banco_codigo)],
    [
      "Agência / instituição",
      disp(
        [config.banco_agencia, config.banco_nome].filter(Boolean).join(" — ") ||
          null
      ),
    ],
  ];
  autoTable(doc, {
    startY: y,
    body: banco,
    styles: { fontSize: SMALL_PT, cellPadding: 1 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 44 } },
    margin: {
      top: contentStartYMm,
      left: SIDE_MARGIN,
      right: SIDE_MARGIN,
      bottom: _contentBottomReserveMm,
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  y = sectionTitle(
    doc,
    "Dados para contrato e ata de registro de preços",
    SIDE_MARGIN,
    y,
    contentW
  );
  const dadosContratoAta = [
    ["Razão social", disp(configRazao(config))],
    ["CNPJ", disp(configCnpj(config))],
    ["Endereço", disp(config.endereco_completo)],
    [
      "Município / UF / CEP",
      `${disp(config.municipio)} / ${disp(config.uf)} / ${disp(config.cep)}`,
    ],
    ["E-mail", disp(config.email)],
    [
      "Telefone(s)",
      disp(
        [config.telefone_1, config.telefone_2].filter(Boolean).join(" / ") ||
          null
      ),
    ],
    ["Representante legal", disp(config.socio_nome)],
    [
      "Inscrições",
      `Municipal ${disp(config.inscricao_municipal)} — Estadual ${disp(config.inscricao_estadual)}`,
    ],
  ];
  autoTable(doc, {
    startY: y,
    body: dadosContratoAta,
    styles: { fontSize: SMALL_PT, cellPadding: 1, overflow: "linebreak" },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 44 } },
    margin: {
      top: contentStartYMm,
      left: SIDE_MARGIN,
      right: SIDE_MARGIN,
      bottom: _contentBottomReserveMm,
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  const rep = [
    ["Nome", disp(config.socio_nome)],
    ["Cargo", disp(config.socio_cargo)],
    ["Nacionalidade", disp(config.socio_nacionalidade)],
    ["Profissão", disp(config.socio_profissao)],
  ];
  autoTable(doc, {
    startY: y,
    head: [["Representante para assinatura", ""]],
    body: rep,
    styles: { fontSize: SMALL_PT, cellPadding: 1 },
    headStyles: { fillColor: [241, 245, 249], textColor: HEAD_RGB },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 44 } },
    margin: {
      top: contentStartYMm,
      left: SIDE_MARGIN,
      right: SIDE_MARGIN,
      bottom: _contentBottomReserveMm,
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  const textoDeclaracaoFecho =
    "Declaramos estar de pleno acordo com as condições do edital e documentos convocados.";
  const textoLocalData = `${disp(localProposta, "")}, ${disp(dataPropostaExtenso, "")}`;
  const alturaFechoAssinatura =
    estimateParagraphBlockHeightMm(doc, textoDeclaracaoFecho, contentW) +
    estimateParagraphBlockHeightMm(doc, textoLocalData, contentW) +
    estimateSignatureBlockHeightMm(Boolean(assImg));
  y = ensureY(doc, y, alturaFechoAssinatura);

  y = paragraph(doc, textoDeclaracaoFecho, SIDE_MARGIN, y, contentW);
  y = paragraph(doc, textoLocalData, SIDE_MARGIN, y, contentW);

  const { marcaRgb, avatarFillRgb, avatarStrokeRgb } = ASSINATURA_CORES;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(SMALL_PT);
  doc.setTextColor(51, 65, 85);
  doc.text("Atenciosamente,", SIDE_MARGIN, y);
  doc.setTextColor(0, 0, 0);
  y += 5;

  const { y: yAfterSig, sigLineW } = signatureBlockPlaceAssinaturaImage(
    doc,
    assImg,
    y,
    contentW
  );
  y = yAfterSig;

  doc.setDrawColor(30, 41, 59);
  doc.setLineWidth(0.3);
  doc.line(SIDE_MARGIN, y, SIDE_MARGIN + sigLineW, y);
  y += 4;

  const badgeR = 7;
  const badgeCx = SIDE_MARGIN + badgeR;
  const badgeCy = y + badgeR;
  doc.setFillColor(...avatarFillRgb);
  doc.setDrawColor(...avatarStrokeRgb);
  doc.setLineWidth(0.15);
  doc.circle(badgeCx, badgeCy, badgeR, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...marcaRgb);
  doc.text(iniciaisNomeCompleto(config.socio_nome), badgeCx, badgeCy + 1.2, {
    align: "center",
  });

  const textLeft = SIDE_MARGIN + badgeR * 2 + 4;
  doc.setTextColor(...marcaRgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(disp(config.socio_nome), textLeft, y + 4);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(SMALL_PT);
  doc.setTextColor(71, 85, 105);
  doc.text(disp(config.socio_cargo), textLeft, y + 9);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(SMALL_PT);
  doc.setTextColor(...marcaRgb);
  doc.text(disp(configRazao(config)), textLeft, y + 14);
  const cnpjFmt = formatCnpjParaExibicao(configCnpj(config));
  doc.text(`CNPJ ${cnpjFmt ?? disp(configCnpj(config))}`, textLeft, y + 19);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  y += 27;

  addCabecalhoEmTodasAsPaginas(doc, cabImg, cabTexto, contentW);
  addRodapeEmTodasAsPaginas(doc, rodImg, rodapeTexto, contentW, rodapeRaster);

  addPageNumbers(doc);
  return doc;
  } finally {
    setPdfLayoutBottomReserveMm(null);
    _contentTopReserveMm = 0;
  }
}

export function buildPropostaPdfFileName(edital) {
  if (!edital) return "Proposta de Preço.pdf";
  const base = (
    edital.titulo_licitacao ||
    edital.instituicao ||
    edital.nome_arquivo ||
    "Documento"
  )
    .replace(/[<>:"/\\|?*]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return `Proposta de Preço - ${base}.pdf`;
}
